if (!customElements.get('stud-sale-badge')) {
  customElements.define('stud-sale-badge', class extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.form) return;
      this.controller = new AbortController();
      document.getElementById(this.dataset.form)?.addEventListener('variant:change', event => {
        const variant = event.detail.variant;
        const regular = Number(variant?.compare_at_price || 0);
        const price = Number(variant?.price || 0);
        this.hidden = !variant || regular <= price;
        this.textContent = this.hidden ? '' : `-${Math.round((regular - price) * 100 / regular)}%`;
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}

if (!customElements.get('stud-pdp-pieces')) {
  customElements.define('stud-pdp-pieces', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.input = this.querySelector('input[name="quantity"]');
      this.addEventListener('change', event => {
        if (event.target.matches('input[type="radio"]')) {
          this.input.value = event.target.value;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.sync();
      }, { signal: this.controller.signal });
      queueMicrotask(() => this.sync());
    }
    disconnectedCallback() {
      this.controller?.abort();
    }
    sync() {
      const quantity = Math.min(Number(this.dataset.max), Math.max(1, Number(this.input.value) || 1));
      this.input.value = String(quantity);
      this.querySelectorAll('input[type="radio"]').forEach(radio => {
        radio.checked = Number(radio.value) === quantity;
      });
      this.querySelector('[data-single-total]').hidden = quantity !== 1;
      this.querySelector('[data-pair-total]').hidden = quantity !== 2;
    }
  });
}


if (!customElements.get('stud-pdp-gallery')) {
  customElements.define('stud-pdp-gallery', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.dialog = this.querySelector('dialog');
      this.addEventListener('click', event => {
        const zoom = event.target.closest('[data-full-image]');
        if (zoom) {
          const image = new Image();
          image.src = zoom.dataset.fullImage;
          image.alt = zoom.querySelector('img')?.alt || '';
          this.querySelector('[data-lightbox-content]').replaceChildren(image);
          this.dialog.showModal();
        } else if (event.target.closest('[data-close]') || event.target === this.dialog) {
          this.dialog.close();
        }
      }, { signal });
      document.getElementById(this.dataset.form)?.addEventListener('variant:change', event => {
        const variant = event.detail.variant;
        if (!variant) return;
        const names = JSON.parse(this.dataset.optionNames).map(name => name.toLowerCase());
        this.querySelectorAll('[data-group-name]').forEach(item => {
          const position = names.indexOf(item.dataset.groupName);
          item.hidden = position >= 0 && String(variant.options[position]).toLowerCase() !== item.dataset.groupValue && String(variant.featured_media?.id) !== item.dataset.mediaId;
        });
      }, { signal });
      const info = this.closest('.product')?.querySelector('.product__info');
      if (info) {
        this.observer = new ResizeObserver(() => {
          this.closest('.product').style.setProperty('--ss-info-height', `${info.getBoundingClientRect().height}px`);
        });
        this.observer.observe(info);
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}

if (!customElements.get('stud-pdp-recommendations')) {
  customElements.define('stud-pdp-recommendations', class extends HTMLElement {
    async connectedCallback() {
      if (this.querySelector('.product-list__inner')) return;
      this.controller = new AbortController();
      for (const intent of ['complementary', 'related']) {
        const url = new URL(`${Shopify.routes.root}recommendations/products`, location.origin);
        url.searchParams.set('product_id', this.getAttribute('product-id'));
        url.searchParams.set('limit', this.getAttribute('recommendations-count'));
        url.searchParams.set('section_id', this.getAttribute('section-id'));
        url.searchParams.set('intent', intent);
        try {
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) continue;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const result = html.querySelector('stud-pdp-recommendations');
          if (result?.querySelector('.product-item')) {
            this.replaceChildren(...result.childNodes);
            return;
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
        }
      }
      this.hidden = true;
    }
    disconnectedCallback() {
      this.controller?.abort();
    }
  });
}


if (!customElements.get('stud-pdp-controller')) {
  customElements.define('stud-pdp-controller', class extends HTMLElement {
    connectedCallback() {
      this.root = document.getElementById(this.dataset.section);
      this.form = document.getElementById(this.dataset.form);
      if (!this.form || !this.root) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.chosenSizes = new Map();
      this.root.addEventListener('change', event => {
        const input = event.target;
        const size = input.closest('[data-required-size]');
        if (size && input.matches('input[type="radio"][data-option-position]')) {
          this.chosenSizes.set(size.dataset.requiredSize, input.value);
        }
        if (input.matches('[data-option-position]')) this.variantPending = true;
        this.syncSizeChoice();
        this.syncTotal();
      }, { capture: true, signal });
      this.form.addEventListener('submit', event => {
        if (!this.sizeChoiceComplete() || this.variantPending) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.root.querySelector('[data-required-size] input[type="radio"]')?.focus();
        }
      }, { capture: true, signal });
      this.form.addEventListener('variant:change', () => queueMicrotask(() => this.syncTotal()), { signal });
      this.form.addEventListener('product:rerender', () => queueMicrotask(() => {
        this.variantPending = false;
        this.syncSizeChoice();
        this.syncTotal();
      }), { signal });
      this.syncSizeChoice();
      this.syncTotal();
      const back = this.root.querySelector('[data-collection-back]');
      if (back) {
        let context;
        try { context = JSON.parse(sessionStorage.getItem('studCollectionReturn') || 'null'); } catch {}
        if (context?.productHandle === back.dataset.currentProduct) {
          try {
            const url = new URL(context.url);
            if (url.origin === location.origin && url.pathname.includes('/collections/')) {
              back.href = url.href;
              back.querySelector('[data-back-label]').textContent = `${back.dataset.prefix} ${context.title}`;
              back.addEventListener('click', () => {
                try { sessionStorage.setItem('studCollectionRestore', JSON.stringify(context)); } catch {}
              }, { signal });
            }
          } catch {}
        }
      }
    }
    sizeChoiceComplete() {
      return Array.from(this.root.querySelectorAll('[data-required-size]')).every(size =>
        Array.from(size.querySelectorAll('input[type="radio"]')).some(input =>
          input.value === this.chosenSizes.get(size.dataset.requiredSize)
        )
      );
    }
    syncSizeChoice() {
      const sizes = this.root.querySelectorAll('[data-required-size]');
      if (!sizes.length) return;
      sizes.forEach(size => {
        const chosen = this.chosenSizes.get(size.dataset.requiredSize);
        size.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === chosen; });
      });
      const blocked = !this.sizeChoiceComplete() || this.variantPending;
      this.root.querySelectorAll('[data-ss-variant-available]').forEach(button => {
        button.disabled = blocked || button.dataset.ssVariantAvailable !== 'true';
      });
    }
    syncTotal() {
      const unitPrice = Number(this.root.querySelector('[data-ss-unit-price]')?.dataset.ssUnitPrice || 0);
      const quantity = Number(Array.from(this.form.elements).find(element => element.name === 'quantity')?.value || 1);
      const total = unitPrice * quantity;
      const formatted = new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
        style: 'currency', currency: this.dataset.currency || 'EUR', minimumFractionDigits: total % 100 ? 2 : 0, maximumFractionDigits: 2
      }).format(total / 100);
      this.root.querySelectorAll('.product-sticky-form__price, [data-sticky-total]').forEach(element => { element.textContent = formatted; });
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}

if (!customElements.get('stud-collection-context')) {
  customElements.define('stud-collection-context', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      document.addEventListener('facet:criteria-changed', event => {
        if (!event.target.closest('.ss-collection-test') || !event.detail?.url) return;
        const url = new URL(event.detail.url, location.href);
        if (this.dataset.previewView) url.searchParams.set('view', this.dataset.previewView);
        else url.searchParams.delete('view');
        event.detail.url = url.href;
      }, { capture: true, signal: this.controller.signal });
      document.addEventListener('click', event => {
        const anchor = event.target.closest('a[href]');
        if (!anchor || !anchor.closest('.ss-collection-test')) return;
        const url = new URL(anchor.href, location.href);
        const match = url.pathname.match(/\/products\/([^/?]+)/);
        if (url.origin !== location.origin || !match) return;
        try {
          sessionStorage.setItem('studCollectionReturn', JSON.stringify({ productHandle: match[1], url: location.href, title: this.dataset.title, scroll: scrollY }));
        } catch {}
      }, { capture: true, signal: this.controller.signal });
      let context;
      try { context = JSON.parse(sessionStorage.getItem('studCollectionRestore') || 'null'); } catch {}
      if (context) {
        try {
          const url = new URL(context.url);
          if (url.pathname === location.pathname) {
            sessionStorage.removeItem('studCollectionRestore');
            const restore = () => requestAnimationFrame(() => requestAnimationFrame(() => scrollTo(0, Number(context.scroll) || 0)));
            if (document.readyState === 'complete') restore();
            else window.addEventListener('load', restore, { once: true, signal: this.controller.signal });
          }
        } catch {}
      }
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}


if (!customElements.get('stud-pdp-size-row')) {
  customElements.define('stud-pdp-size-row', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('change', () => this.revealSelection(), { signal });
      this.addEventListener('focusin', () => this.revealSelection(), { signal });
      this.frame = requestAnimationFrame(() => this.revealSelection());
    }
    revealSelection() {
      const selected = this.querySelector('input:checked + label, .is-selected');
      if (!selected) return;
      const row = this.getBoundingClientRect();
      const choice = selected.getBoundingClientRect();
      if (choice.right > row.right - 2) this.scrollLeft += choice.right - row.right + 2;
      else if (choice.left < row.left + 2) this.scrollLeft += choice.left - row.left - 2;
    }
    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
    }
  });
}


// Mirror the actual Yotpo earning amount in the native benefits accordion.
if (!customElements.get('stud-pdp-loyalty')) {
  customElements.define('stud-pdp-loyalty', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.ss-pdp-test');
      if (!this.root) return;
      this.details = this.querySelector('details');
      this.amount = this.querySelector('[data-loyalty-label]');
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.root, { childList: true, subtree: true, characterData: true });
      this.sync();
    }
    sync() {
      const widget = this.root.querySelector('#yotpo-product-points-widget');
      const source = widget?.closest('.shopify-app-block');
      const amount = widget?.querySelector('.yotpo-product-points-widget-points-amount')?.textContent.match(/\d[\d\s.,]*/)?.[0].trim();
      if (!amount) {
        this.details.hidden = true;
        source?.classList.remove('ss-loyalty-source');
        return;
      }
      const label = this.amount.dataset.labelTemplate.replace('__POINTS__', amount);
      if (this.amount.textContent !== label) this.amount.textContent = label;
      if (this.details.hidden) this.details.hidden = false;
      source?.classList.add('ss-loyalty-source');
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  });
}

if (!customElements.get('stud-facet-sort')) {
  customElements.define('stud-facet-sort', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.addEventListener('change', event => {
        if (!event.target.matches('select[name="sort_by"]')) return;
        const url = new URL(location.pathname, location.origin);
        url.search = new URLSearchParams(new FormData(event.target.form)).toString();
        this.closest('facet-filters').dispatchEvent(new CustomEvent('facet:criteria-changed', {
          bubbles: true, detail: { url: url.href }
        }));
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}

if (!customElements.get('stud-collection-count')) {
  customElements.define('stud-collection-count', class extends HTMLElement {
    connectedCallback() {
      const results = document.querySelector('.ss-collection-test #facet-main');
      if (!results) return;
      const sync = () => {
        const count = results.querySelector('[data-ss-result-count]')?.textContent.trim();
        if (count && this.textContent !== count) this.textContent = count;
      };
      this.observer = new MutationObserver(sync);
      this.observer.observe(results, { childList: true, subtree: true });
      sync();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  });
}
